import * as T from '../types';
import { addDays, eventTime, time } from '../labels';

/** One line of a board cell: an event or a task, with the time it is shown at. */
interface BoardItem {
  key: string;
  kind: 'event' | 'task';
  title: string;
  /** "15:00–17:00", "Dia todo" or a task's "09:00"; empty for a task without a time. */
  when: string;
  detail?: string | null;
  isDone: boolean;
}

/**
 * What a column has on `day`: all-day events first, then events and tasks by time, then the tasks without a time.
 * An event of several days shows on each of them.
 */
export function boardItems(column: T.CalendarBoardColumn, day: T.DateOnly): BoardItem[] {
  const sorted: { order: string; item: BoardItem }[] = [];
  column.events
    .filter(e => e.date <= day && e.endDate >= day)
    .forEach(e => {
      // On a later day of a timed event spanning days, it is already going on: with the all-day ones
      const allDay = e.isAllDay || e.date < day;
      sorted.push({
        order: allDay ? '0' : `1${time(e.startTime)}`,
        item: { key: `e${e.publicId}`, kind: 'event', title: e.title, when: eventTime(e), detail: e.location, isDone: false },
      });
    });
  column.tasks
    .filter(t => t.date === day)
    .forEach(t => sorted.push({
      order: t.dueTime ? `1${time(t.dueTime)}` : '2',
      item: { key: `t${t.publicId}`, kind: 'task', title: t.title, when: time(t.dueTime), isDone: t.isDone },
    }));
  // Stable: the server's order stays within the same time
  return sorted
    .map((entry, index) => ({ ...entry, index }))
    .sort((a, b) => (a.order === b.order ? a.index - b.index : a.order < b.order ? -1 : 1))
    .map(entry => entry.item);
}

/** The days the board shows: one, or the seven from `from`. */
export function boardDays(from: T.DateOnly, days: 1 | 7): T.DateOnly[] {
  return Array.from({ length: days }, (_, i) => addDays(from, i));
}
